/**
 *  @fileOverview Tooltip handler
 *
 *  @author       Peter Schmiz <peter.schmiz@possible.com>
 *
 *  @requires     NPM:lodash
 *  @requires     NPM:detect-it
 */

import 'whatwg-fetch';
import queryString from 'query-string';
import throttle from 'lodash/throttle';
import detectIt from 'detect-it';
import { tooltipController } from './tooltip-controller';

const ACTIVE_CLASS = 'tooltip--active';

const ACTIVATE_KEYCODES = [13, 32, 38, 40];
const DEACTIVATE_KEYCODES = [27];

const APIHOST = window.location.hostname.match(/localhost|10\.|127\./ig) ? 'http://futurefocus.possible.hu' : '';

class Tooltip {
  constructor(el) {
    if (!(typeof el && el.nodeName)) {
      throw TypeError('Constructor parameter should be a valid DOM element!');
    }

    this.isTooltipActive = false;
    this.tooltipDirection = 'down';
    this.contentLoaded = false;
    this.isLoading = false;
    this.tooltip = el;
    this.id = el.getAttribute('data-tooltip');

    this.initDOMElements();
    this.initBindings();
  }

  initDOMElements() {
    this.wrapper = this.tooltip.querySelector('[data-tooltip-wrapper]');
    this.body = this.tooltip.querySelector('[data-tooltip-body]');
    this.closeButton = this.tooltip.querySelector('[data-tooltip-close]');
  }

  initBindings() {
    this.tooltip.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    }, false);

    this.tooltip.addEventListener('focus', (e) => {
      e.preventDefault();
      this.open(true);
    }, false);

    this.tooltip.addEventListener('keydown', (e) => {
      if (ACTIVATE_KEYCODES.includes(e.keyCode) && this.isTooltipActive === false) {
        this.open();
      } else if (DEACTIVATE_KEYCODES.includes(e.keyCode) && this.isTooltipActive === true) {
        this.close();
      }
    }, false);

    if (this.wrapper !== null) {
      this.wrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, false);

      this.wrapper.addEventListener('blur', () => {
        if (this.isTooltipActive === true && detectIt.deviceType !== 'mouseOnly') {
          this.closeButton.focus();
        }
      }, false);
    }

    if (this.closeButton !== null) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }, false);

      this.closeButton.addEventListener('keydown', (e) => {
        e.preventDefault();
        if (detectIt.deviceType !== 'mouseOnly') {
          if ((e.keycode === 9) || (e.keycode === 9 && e.keycode === 16)) {
            e.preventDefault();
            this.closeButton.focus();
          }
        } else if (e.keycode === 13) {
          this.close();
        }
      }, false);
    }

    window.addEventListener('resize', throttle(() => {
      if (this.isTooltipActive) {
        this.wrapper.removeAttribute('style');
        this.checkSpace();
      }
    }, 50), detectIt.passiveEvents ? { capture: false, passive: true } : false);
  }

  loadTooltip(doNotOpen = false) {
    if (doNotOpen === false) {
      this.isLoading = true;
    }
    return fetch(`${APIHOST}/api/tooltip`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: queryString.stringify({ id: this.id }),
    });
  }

  open(doNotOpen = false) {
    if (this.contentLoaded === false && this.isLoading === false) {
      this.loadTooltip(doNotOpen).then(response => response.json()).then((body) => {
        if (body.IsSuccess === true && body.Result) {
          this.body.innerHTML = JSON.parse(body.Result).Text;
          this.contentLoaded = true;
          this.isLoading = false;
          if (doNotOpen !== true) {
            this.showTooltip();
          }
        }
      }).catch((result) => {
        this.isLoading = false;
        console.warn(result);
      });
    } else if (doNotOpen !== true && this.contentLoaded === true) {
      this.showTooltip();
    }
  }

  showTooltip() {
    tooltipController.closeAllTooltips();
    this.isTooltipActive = true;
    this.wrapper.setAttribute('aria-hidden', false);
    this.tooltip.classList.remove('tooltip--direction-up');
    this.tooltip.classList.remove('tooltip--direction-down');
    this.tooltip.classList.add(ACTIVE_CLASS);
    if (detectIt.deviceType !== 'mouseOnly') {
      this.setMobileFocus(true);
    }
    this.checkSpace();
  }

  close() {
    this.isTooltipActive = false;
    this.wrapper.setAttribute('aria-hidden', true);
    this.wrapper.removeAttribute('style');
    this.tooltip.classList.remove(ACTIVE_CLASS);
    this.tooltip.classList.remove('tooltip--direction-up');
    this.tooltip.classList.remove('tooltip--direction-down');
    if (detectIt.deviceType !== 'mouseOnly') {
      this.setMobileFocus(false);
    }
  }

  checkSpace() {
    const wrapperHeight = this.wrapper.offsetHeight;
    const boundingRectangle = this.wrapper.getBoundingClientRect();
    const wrapperOffsetY = boundingRectangle.top + window.scrollY;
    let correctionX = null;

    if (detectIt.deviceType === 'mouseOnly') {
      if (boundingRectangle.x + boundingRectangle.width > window.innerWidth) {
        correctionX = ((boundingRectangle.x + boundingRectangle.width) - window.innerWidth);
        this.wrapper.style.left = `calc(50% - ${correctionX}px)`;
      } else if (boundingRectangle.x < 0) {
        correctionX = Math.abs(boundingRectangle.x);
        this.wrapper.style.left = `calc(50% + ${correctionX}px)`;
      }
    }

    if ((window.innerHeight - wrapperOffsetY) + window.scrollY < wrapperHeight) {
      this.tooltipDirection = 'up';
    } else {
      this.tooltipDirection = 'down';
    }
    this.tooltip.classList.add(`tooltip--direction-${this.tooltipDirection}`);
  }

  setMobileFocus(status = false) {
    if (status === true) {
      this.wrapper.setAttribute('tabindex', 0);
      this.closeButton.focus();
    } else {
      this.wrapper.removeAttribute('tabindex');
      this.tooltip.focus();
    }
  }
}

export default Tooltip;



// WEBPACK FOOTER //
// ./src/js/modules/tooltip.js